import { useState } from "react"
import Swal from "sweetalert2"
import { GET, POST, PUT } from "../../../../services/api"
import { AggregatorClassification, CreateAggregatorClassificationDto, TableParams, UpdateAggregatorClassificationDto } from "../aggregadorCategory.type"

const URL = 'aggregator-classification'


export function useAggregatorCategory(isSubcategory: boolean = false) {


    const [aggregatorCategories, setAggregatorCategories] = useState<AggregatorClassification[]>([])
    const [loading, setLoading] = useState(false)
    const [tableLoading, setTableLoading] = useState(false)

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
        total: 0,
        pageSizes: [10, 25, 50, 100],
    })
    const [sorting, setSorting] = useState({
        field: 'id',
        direction: 'asc' as 'asc' | 'desc',
    })
    const [searchTerm, setSearchTerm] = useState('')



    const handleDataChange = async (params: {
        page: number
        pageSize: number
        search: string
        sorting?: { field: string; direction: 'asc' | 'desc' }
        filters?: any
    }) => {
        setPagination((prev) => ({ ...prev, page: params.page, pageSize: params.pageSize }))
        if (params.sorting) setSorting(params.sorting)
        setSearchTerm(params.search)
        return await getAggregatorClassification(params)
    }


    const handlePaginationChange = async (newPagination: any) => {
        setPagination(newPagination)
        return await getAggregatorClassification({
            page: newPagination.page,
            pageSize: newPagination.pageSize,
            search: searchTerm,
            sorting: sorting.field ? sorting : { field: 'id', direction: 'asc' },
        })
    }

    const handleSortingChange = async (newSorting: any) => {
        setSorting(newSorting)
        return await getAggregatorClassification({
            page: pagination.page,
            pageSize: pagination.pageSize,
            search: searchTerm,
            sorting: newSorting,
        })
    }

    async function getAggregatorClassification(params?: TableParams) {
        try {
            setTableLoading(true)
            const currentPage = params?.page || pagination.page
            const currentPageSize = params?.pageSize || pagination.pageSize
            const currentSearch = params?.search || searchTerm
            const currentSorting = params?.sorting || sorting

            const query = `?field=${currentSorting.field}&order=${currentSorting.direction.toUpperCase()}&page=${currentPage}&take=${currentPageSize}&search=${currentSearch}`



            const request = await GET(`${URL}${isSubcategory ? '/subcategory' : '/category'}${query}`)
            console.log('[getAggregatorClassification] Resposta da API:', request)
            if (!request.data) {
                console.warn('[getAggregatorClassification] Nenhum dado retornado da API!')
            }

            setAggregatorCategories(request.data)

            if (request.meta) {
                const paginagionInfo = {
                    total: request.meta.itemCount,
                    page: currentPage,
                    pageSize: currentPageSize,
                }
                setPagination((prev) => ({
                    ...prev,
                    ...paginagionInfo,
                }))
                console.log('[getAggregatorClassification] Paginação atualizada:', paginagionInfo)
            }
            return request.data
        } catch (error) {
            console.log('[getAggregatorClassification] Erro:', error)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Um erro ocorreu ao filtrar',
            })
            return []
        } finally {
            setTableLoading(false)
        }
    }

    async function getAggregatorClassificationById(id: number) {
        try {
            const request = await GET(`${URL}/${id}`)

            return request
        } catch (error) {
            console.log('[getAggregatorClassification] Erro:', error)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Um erro ocorreu ao filtrar',
            })
            return []
        } finally {
            setTableLoading(false)
        }
    }

    async function createAggregatorClassification(data: CreateAggregatorClassificationDto) {
        try {

            const isValid = validateForm(data)
            if (!isValid) return

            setLoading(true)
            const request = await POST(`${URL}`, data)
            console.log('[createAggregatorClassification] Resposta da API:', request)
            if (request.data) {
                Swal.fire({
                    icon: 'success',
                    title: 'Sucesso',
                    text: 'Categoria criada com sucesso!',
                })
                await getAggregatorClassification()
            }
        } catch (error) {
            console.log('[createAggregatorClassification] Erro:', error)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Um erro ocorreu ao criar a categoria',
            })
        } finally {
            setLoading(false)
        }
    }

    async function updateAggregatorClassification(id: number, data: UpdateAggregatorClassificationDto) {
        try {
            const isValid = validateForm(data)
            if (!isValid) return
            setLoading(true)
            const request = await PUT(`${URL}/${id}`, data)
            console.log('[updateAggregatorClassification] Resposta da API:', request)
            if (request.data) {
                Swal.fire({
                    icon: 'success',
                    title: 'Sucesso',
                    text: 'Categoria atualizada com sucesso!',
                })
                await getAggregatorClassification()
            }
        } catch (error) {
            console.log('[updateAggregatorClassification] Erro:', error)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Um erro ocorreu ao atualizar a categoria',
            })
        } finally {
            setLoading(false)
        }
    }

    async function deleteAggregatorClassification(id: number) {
        try {
            setLoading(true)
            const request = await POST(`${URL}/delete`, { id })
            console.log('[deleteAggregatorClassification] Resposta da API:', request)
            if (request.data) {
                Swal.fire({
                    icon: 'success',
                    title: 'Sucesso',
                    text: 'Categoria deletada com sucesso!',
                })
                await getAggregatorClassification()
            }
        } catch (error) {
            console.log('[deleteAggregatorClassification] Erro:', error)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Um erro ocorreu ao deletar a categoria',
            })
        } finally {
            setLoading(false)
        }
    }

    function validateForm(data: CreateAggregatorClassificationDto | UpdateAggregatorClassificationDto) {
        if (!data.name || data.name.trim() === '') {
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'O campo "Nome" é obrigatório.',
            })
            return false
        }
        if (!data.pluginspaceSegmentId) {
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'O campo "Setor" é obrigatório.',
            })
            return false
        }

        if (!data.svgwebappId) {
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'O campo "Ícone" é obrigatório.',
            })
            return false
        }

        return true


    }

    async function getAggregatorAllClassification(params?: TableParams) {
        try {
            setTableLoading(true)
            const currentPage = params?.page || pagination.page
            const currentPageSize = 5000
            const currentSearch = params?.search || searchTerm
            const currentSorting = params?.sorting || sorting

            const query = `?field=${currentSorting.field}&order=${currentSorting.direction.toUpperCase()}&page=${currentPage}&take=${currentPageSize}&search=${currentSearch}`



            const request = await GET(`${URL}${query}`)
            console.log('[getAggregatorAllClassification] Resposta da API:', request)
            if (!request.data) {
                console.warn('[getAggregatorAllClassification] Nenhum dado retornado da API!')
            }
            return request.data
        } catch (error) {
            console.log('[getAggregatorClassification] Erro:', error)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Um erro ocorreu ao filtrar',
            })
            return []
        } finally {
            setTableLoading(false)
        }
    }

    async function updateAggregatorClassificationOrder(
        data: AggregatorClassification[]
    ) {
        try {
            setTableLoading(true)
            const request = await PUT(`${URL}/ordenation`, data)
            console.log('[updateAggregatorClassificationOrder] Resposta da API:', request)
            setAggregatorCategories(data)
        } catch (error) {
            console.log('[updateAggregatorClassification] Erro:', error)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Um erro ocorreu ao atualizar a categoria',
            })
        } finally {
            setTableLoading(false)
        }
    }


    return {
        aggregatorCategories,
        loading,
        setLoading,
        tableLoading,
        pagination,
        sorting,
        searchTerm,
        handleDataChange,
        handlePaginationChange,
        handleSortingChange,
        getAggregatorClassification,
        createAggregatorClassification,
        updateAggregatorClassification,
        deleteAggregatorClassification,
        setTableLoading,
        getAggregatorClassificationById,
        getAggregatorAllClassification,
        updateAggregatorClassificationOrder
    }
}